/**
 * Tenant branding at boot (M2.9, FR-UI-03, FR-PLAT-03).
 *
 * `GET /api/v1/public/tenant` answers for whichever clinic the *host* resolved to, so
 * the app never asks which tenant it is - the URL already decided (ARCHITECTURE.md
 * §4.1). What comes back is applied in exactly one way: **CSS custom properties on the
 * root element**. There is no theme provider and no re-render, because every component
 * in `@platform/ui` already compiles to `var(--color-primary)` and friends
 * (packages/ui/README.md, "Tenant theming contract").
 *
 * ## What may be overridden
 *
 * The blue family, a typeface from a closed list, the logo and the favicon. Neutrals,
 * status colours, the type scale, radii and spacing are platform-wide: a clinic brands its
 * accent, not the clinical grammar of the product. Stored hover, subtle and border colours
 * are used when set and derived from the primary with `color-mix` when not (M17.3,
 * `packages/ui/src/lib/tenant-branding.ts`).
 *
 * ## Features
 *
 * The same response is where `funnel.*` flags come from (flags.ts layer 2). The contract
 * carries them as `features.ui` - `tenant_features` rows written under the `ui.` prefix
 * and returned with the prefix stripped, so a super admin writing `ui.funnel.fewerSteps`
 * is the whole registration mechanism (API.md §2.3). `readFeatures` turns that list into
 * the map `setTenantFeatures` wants; anything that is not a `funnel.` name is ignored,
 * which is what lets the list grow without this bundle knowing.
 *
 * ## Booking configuration
 *
 * The same response also carries the state list, the visit types, the consent texts and
 * the booking window (M5.7). Those go to `booking/lib/tenant-config`, which is what the
 * funnel's pages read; before it arrives they read the live page's own eight states and
 * two types, which is the parity build (ADR-008).
 */

import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';

import type { ApiClient, ApiError, PublicTenant } from '@platform/api-client';
import { useApiClient } from '@platform/api-client/react';
import { applyTenantBranding as applyTenantTheme } from '@platform/ui';

import { setBookingConfig } from '../../booking/lib/tenant-config';
import { isFlagKey, setTenantFeatures, type FlagKey, type TenantFeatures } from '../../flags';

export const TENANT_QUERY_KEY = ['public-tenant'] as const;

export function tenantQueryOptions(
  client: ApiClient,
): UseQueryOptions<PublicTenant, ApiError, PublicTenant, typeof TENANT_QUERY_KEY> {
  return {
    queryKey: TENANT_QUERY_KEY,
    queryFn: ({ signal }) => client.getPublicTenant({ signal }),
    /*
     * 60 s, matching the API's own `private, max-age=60` (M17.3). It was five minutes,
     * which meant a clinic that saved a new colour could reload and still see the old one;
     * the logo and favicon URLs are versioned now, so a fresh payload is all it takes.
     */
    staleTime: 60_000,
    retry: 1,
  };
}

/** Branding, legal links and the clinic's name. */
export function useTenant(): UseQueryResult<PublicTenant, ApiError> {
  const client = useApiClient();
  return useQuery(tenantQueryOptions(client));
}

/**
 * The tenant's boot: paints its brand and publishes its feature flags and booking config.
 *
 * The painting is `@platform/ui`'s `applyTenantBranding` since M17.3 — the same function
 * the clinic admin shell uses (FR-UI-03) — so stored hover/subtle/border colours win over
 * the `color-mix` derivation, an allowed `fontFamily` sets `--font-sans`, and the logo URL
 * carries the branding version. Idempotent, and safe with the platform's own branding: a
 * value that is absent or is the platform default removes the override rather than
 * restating it, so HeyAllergy's hand-tuned palette stays in force (and the parity build
 * with it). The favicon and the Open Graph tags are `applyDocumentBranding`'s, called from
 * `routes/__root.tsx` beside the title.
 */
export function applyTenantBranding(tenant: PublicTenant): void {
  applyTenantTheme({ ...tenant.branding, brandVersion: brandVersionOf(tenant) });

  setTenantFeatures(readFeatures(tenant));
  setBookingConfig(tenant);
}

/**
 * `branding.brandVersion` (M17.1): the branding row's `updatedAt` epoch. The API already
 * stamps it onto the logo and favicon paths as `?v=`; it is passed along anyway so a URL
 * that arrives without one is versioned here (`versionedAssetUrl` leaves a stamped URL
 * alone). Read defensively, because a bundle must survive an older API.
 */
export function brandVersionOf(tenant: PublicTenant | undefined): number | null {
  const raw: unknown = tenant?.branding.brandVersion;
  return typeof raw === 'number' && raw > 0 ? raw : null;
}

/** The clinic's display name, with the platform name as the fallback. */
export function clinicName(tenant: PublicTenant | undefined): string {
  return tenant?.branding.clinicName ?? tenant?.tenant.name ?? 'HeyAllergy';
}

/**
 * `features.ui` as the flag store reads it (M5.7, API.md §2.3, ADR-008, rules §44).
 *
 * The API answers a LIST of switched-on presentation flags - `["funnel.fewerSteps"]` -
 * and the store wants a MAP. The translation is one line, and the two rules around it
 * are the point:
 *
 *  - **presence means on.** There is no `false` entry, because a flag a tenant has not
 *    switched on is a flag at its default, and the default is off (`FLAG_DEFAULTS`).
 *  - **anything this bundle does not recognise is ignored**, not an error. A clinic may
 *    be running a flag a newer bundle introduced, or one an older bundle has already
 *    deleted, and neither may break the page (rules §44.4).
 *
 * `flagOverrides` decides whether `?flags=` on the URL may still win. It is false unless
 * the clinic wrote the `ui.flagOverrides` row, so a pasted demo link does nothing on a
 * production tenant.
 *
 * A response with no `features` at all means "no tenant features", which is the parity
 * build. It is read defensively because a bundle must survive an older API.
 */
function readFeatures(tenant: PublicTenant): TenantFeatures | null {
  const raw = (tenant as { features?: unknown }).features;
  if (typeof raw !== 'object' || raw === null) return null;

  const block = raw as { ui?: unknown; flagOverrides?: unknown };
  const flags: Partial<Record<FlagKey, boolean>> = {};

  if (Array.isArray(block.ui)) {
    for (const entry of block.ui) {
      if (typeof entry !== 'string') continue;
      if (!entry.startsWith('funnel.')) continue;
      if (!isFlagKey(entry)) continue;
      flags[entry] = true;
    }
  }

  return {
    flags,
    flagOverrides: block.flagOverrides === true,
  };
}
